import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ChiroUpDayJsCommon } from '../constants/stringConstants';
import {
  OnboardingAction,
  OnboardingAuthor,
  OnboardingAvailability,
  OnboardingAvailabilityInput,
  OnboardingCategory,
  OnboardingChecklistProgress,
  OnboardingCompletion,
  OnboardingConfig,
  OnboardingContent,
  OnboardingCourse,
  OnboardingFile,
  OnboardingFileCategory,
  OnboardingState,
  OnboardingTask,
  OnboardingTaskProgress,
  OnboardingTrainingProgress,
  OnboardingView,
  OnboardingViewInput,
} from '../types/Onboarding.type';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

/** Calendar dates are advanced in UTC only to obtain the next date label;
 * each clinic midnight is then parsed independently in its own timezone. */
export const onboardingAvailability = ({
  hasEHR,
  goLiveDate,
  timezone = ChiroUpDayJsCommon.defaultTimezone,
  now = new Date().toISOString(),
}: OnboardingAvailabilityInput): OnboardingAvailability => {
  if (!goLiveDate) return { active: hasEHR, expiresAt: null, daysUntilGoLive: null };

  const calendar = dayjs.utc(goLiveDate);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(goLiveDate) || calendar.format('YYYY-MM-DD') !== goLiveDate) {
    return { active: hasEHR, expiresAt: null, daysUntilGoLive: null };
  }

  const expiryDate = calendar.add(14, 'day').format('YYYY-MM-DD');
  const expiresAt = dayjs.tz(expiryDate, timezone).toISOString();
  const today = dayjs(now).tz(timezone).format('YYYY-MM-DD');

  return {
    active: hasEHR && dayjs(now).valueOf() < dayjs(expiresAt).valueOf(),
    expiresAt,
    daysUntilGoLive: calendar.diff(dayjs.utc(today), 'day'),
  };
};

const completion = (statuses: Array<OnboardingTaskProgress['status']>): OnboardingCompletion => {
  const applicable = statuses.filter(status => status !== 'not_applicable');
  const completed = applicable.filter(status => status === 'done').length;

  return {
    status: !applicable.length
      ? 'not_applicable'
      : completed === applicable.length
        ? 'done'
        : completed > 0 || applicable.length < statuses.length
          ? 'in_progress'
          : 'not_started',
    percent: applicable.length ? Math.round((completed / applicable.length) * 100) : 0,
    completed,
    applicable: applicable.length,
  };
};

export const onboardingChecklist = (
  tasks: OnboardingTask[],
  progress: Record<string, OnboardingTaskProgress>,
  categories: OnboardingCategory[]
): OnboardingChecklistProgress => {
  const results = categories.flatMap(category => {
    const categoryTasks = tasks.filter(task => task.categoryId === category.id);
    if (!categoryTasks.length) return [];

    return [
      {
        ...category,
        ...completion(categoryTasks.map(task => progress[task.id]?.status || 'not_started')),
      },
    ];
  });
  const applicable = results.filter(category => category.status !== 'not_applicable');
  const completed = applicable.filter(category => category.status === 'done').length;
  const started = results.some(category => category.status !== 'not_started');

  return {
    categories: results,
    checklist: {
      status: !applicable.length
        ? 'not_applicable'
        : completed === applicable.length
          ? 'done'
          : started
            ? 'in_progress'
            : 'not_started',
      percent: applicable.length ? Math.round((completed / applicable.length) * 100) : 0,
      completed,
      applicable: applicable.length,
    },
  };
};

export const onboardingCourseProgress = (
  course: OnboardingCourse,
  progress?: OnboardingTrainingProgress
): OnboardingCompletion => {
  if (progress?.dismissed) {
    return { status: 'not_applicable', percent: 0, completed: 0, applicable: 0 };
  }

  return completion(
    course.topics
      .filter(topic => topic.active && !topic.retired)
      .map(topic => progress?.topics[topic.id] || 'not_started')
  );
};

export const createOnboardingState = (
  content: OnboardingContent,
  enrolledAt: string
): OnboardingState => ({
  enrolledAt,
  config: {
    goLiveDate: null,
    migrationDate: null,
    testFileDueDate: null,
    finalFileDueDate: null,
    checklistDueDate: null,
    coachId: null,
    migrationApplicable: true,
    consentRequired: false,
    readiness: 'not_started',
  },
  checklistTasks: content.tasks
    .filter(task => task.active !== false)
    .map(task => ({
      ...task,
      resources: task.resources.map(resource => ({ ...resource })),
    })),
  checklistCategories: content.categories.map(category => ({ ...category })),
  taskProgress: {},
  training: {},
  files: [],
});

/** Existing clinics retain their task IDs, category assignments, applicability
 * rules, and removed tasks. Only copy and ordering follow the live catalog. */
export const mergeOnboardingChecklist = (
  snapshot: OnboardingTask[],
  content: OnboardingContent
): OnboardingTask[] => {
  const live = new Map(content.tasks.map((task, index) => [task.id, { task, index }]));

  return snapshot
    .map((task, index) => {
      const latest = live.get(task.id);
      return {
        task: latest
          ? {
              ...task,
              title: latest.task.title,
              description: latest.task.description,
              settingUrl: latest.task.settingUrl,
              resources: latest.task.resources.map(resource => ({ ...resource })),
            }
          : { ...task, resources: task.resources.map(resource => ({ ...resource })) },
        order: latest?.index ?? content.tasks.length + index,
      };
    })
    .sort((a, b) => a.order - b.order)
    .map(item => item.task);
};

export const updateOnboardingTask = (
  task: OnboardingTask,
  progress: OnboardingTaskProgress | undefined,
  action: Extract<OnboardingAction, { type: 'task' }>,
  author: OnboardingAuthor,
  at: string
): OnboardingTaskProgress => {
  if (action.status === 'not_applicable' && task.required) {
    throw new Error('Required tasks cannot be marked Not Applicable.');
  }

  const previous: OnboardingTaskProgress = progress || {
    status: 'not_started',
    flagged: false,
    conversation: { status: 'open', messages: [] },
  };
  const status = action.status ?? previous.status;
  const finished = status !== 'not_started';
  const statusChanged = action.status !== undefined && action.status !== previous.status;

  return {
    ...previous,
    status,
    flagged: finished ? false : (action.flagged ?? previous.flagged),
    ...(statusChanged ? { changedBy: { ...author }, changedAt: at } : {}),
    conversation: {
      ...previous.conversation,
      status: action.status !== undefined && finished ? 'resolved' : previous.conversation.status,
      messages: [...previous.conversation.messages],
    },
  };
};

export const updateOnboardingTraining = (
  progress: OnboardingTrainingProgress | undefined,
  action: Extract<OnboardingAction, { type: 'training' }>,
  course: OnboardingCourse
): OnboardingTrainingProgress => {
  if (!course.active || course.retired) throw new Error('This course is no longer active.');
  if (
    action.topicId &&
    !course.topics.some(topic => topic.id === action.topicId && topic.active && !topic.retired)
  ) {
    throw new Error('This topic is no longer active.');
  }

  return {
    dismissed: action.dismissed ?? progress?.dismissed ?? false,
    topics: {
      ...progress?.topics,
      ...(action.topicId && action.status ? { [action.topicId]: action.status } : {}),
    },
  };
};

export const canMarkMigrationReady = (config: OnboardingConfig, files: OnboardingFile[]): boolean =>
  config.migrationApplicable &&
  (!config.consentRequired ||
    files.some(
      file =>
        !file.deletedAt &&
        file.category === 'consent' &&
        file.versions[file.versions.length - 1]?.reviewStatus === 'accepted'
    ));

export const onboardingReadinessAfterSubmission = (config: OnboardingConfig): OnboardingConfig => ({
  ...config,
  readiness: config.readiness === 'ready' ? 'in_progress' : config.readiness,
});

/** The caller supplies the current curriculum while active and its historical
 * cutoff version while expired. No private state object is returned wholesale. */
export const buildOnboardingView = ({
  state,
  content,
  userId,
  isAdmin,
  internal = false,
  hasEHR,
  timezone = ChiroUpDayJsCommon.defaultTimezone,
  now = new Date().toISOString(),
  team = [],
  locationWarnings = [],
}: OnboardingViewInput): OnboardingView => {
  const availability = onboardingAvailability({
    hasEHR,
    goLiveDate: state.config.goLiveDate,
    timezone,
    now,
  });
  const tasks = mergeOnboardingChecklist(state.checklistTasks, content);
  // Preserve category names for removed catalog categories so enrolled tasks
  // remain visible and continue contributing to clinic progress.
  const categories = [...content.categories];
  for (const task of tasks) {
    if (!categories.some(category => category.id === task.categoryId)) {
      categories.push(
        state.checklistCategories?.find(category => category.id === task.categoryId) || {
          id: task.categoryId,
          title: task.categoryId,
          description: '',
        }
      );
    }
  }
  const result = onboardingChecklist(tasks, state.taskProgress, categories);
  const canTrack = internal || availability.active;
  const canSeeChecklist = canTrack && (internal || isAdmin);
  const canSeeFiles = canTrack && (internal || (isAdmin && state.config.migrationApplicable));
  const coach =
    content.coaches.find(item => item.id === state.config.coachId && item.active) || null;
  const courses = content.courses
    .filter(course => internal || (course.active && !course.retired))
    .map(course => ({
      ...course,
      topics: course.topics.filter(topic => internal || (topic.active && !topic.retired)),
    }));
  const today = dayjs(now).tz(timezone).format('YYYY-MM-DD');
  const files = canSeeFiles
    ? state.files
        .filter(file => internal || !file.deletedAt)
        .map(file => ({
          ...file,
          versions: internal ? file.versions : file.versions.slice(-1),
        }))
    : [];
  const migrationOverdue: Record<OnboardingFileCategory, boolean> = {
    test: false,
    final: false,
    consent: false,
  };
  if (canSeeFiles && state.config.migrationApplicable) {
    for (const category of ['test', 'final'] as const) {
      const due =
        category === 'test' ? state.config.testFileDueDate : state.config.finalFileDueDate;
      const submissions = state.files.filter(file => file.category === category && !file.deletedAt);
      migrationOverdue[category] =
        !!due &&
        due < today &&
        (!submissions.length ||
          submissions.some(
            file => file.versions[file.versions.length - 1]?.reviewStatus !== 'accepted'
          ));
    }
  }

  return {
    availability,
    config: {
      ...state.config,
      coachId: coach?.id || null,
      readiness: canTrack ? state.config.readiness : 'not_started',
    },
    content: {
      ...content,
      tasks: canSeeChecklist ? tasks : [],
      categories: canSeeChecklist ? categories : [],
      coaches: internal ? content.coaches : coach ? [coach] : [],
      courses: canTrack ? courses : [],
    },
    coach,
    checklist: canTrack
      ? result.checklist
      : { status: 'not_started', percent: 0, completed: 0, applicable: 0 },
    categories: canSeeChecklist ? result.categories : [],
    checklistOverdue:
      canSeeChecklist &&
      !!state.config.checklistDueDate &&
      state.config.checklistDueDate < today &&
      result.checklist.applicable > result.checklist.completed,
    migrationOverdue,
    tasks: canSeeChecklist ? tasks : [],
    taskProgress: canSeeChecklist
      ? Object.fromEntries(
          tasks
            .map(task => [task.id, state.taskProgress[task.id]])
            .filter(([, progress]) => !!progress)
        )
      : {},
    training: canTrack ? state.training[userId] || {} : {},
    team: canSeeChecklist
      ? team
          .filter(member => internal || member.active)
          .filter(member => availability.active || state.training[member.id] !== undefined)
          .map(member => ({
            ...member,
            training: state.training[member.id] || {},
          }))
      : [],
    files,
    locationWarnings: canSeeChecklist ? locationWarnings : [],
  };
};
